import { createSlice } from '@reduxjs/toolkit';
import { setPlayerPropertyById } from './gameSlice';
import { getMaxPlayerHealth } from '../playerUtils';

export const slice = createSlice({
  name: 'animation',
  initialState: {
    playersReceivingEffect: {},
    playerBeingDamaged: {},

    // Flags
    isCurrentlyShuffling: false,

    // Animations (generic)
    animationQueue: []
  },
  reducers: {
    addToAnimationQueueInternal: (state, { payload }) => {
      state.animationQueue.push(payload);
    },
    shiftAnimationQueue: state => {
      state.animationQueue.shift();
    },
    clearAnimationQueue: state => {
      state.animationQueue = [];
    },
    setCurrentlyShuffling: (state, { payload }) => {
      state.isCurrentlyShuffling = payload;
    },
    markPlayerReceivingEffect: (state, { payload }) => {
      state.playersReceivingEffect[payload.id] = payload.state;
    },
    markPlayerBeingDamaged: (state, { payload }) => {
      state.playerBeingDamaged[payload.id] = payload.state;
    },
  },
});

export const {
  shiftAnimationQueue, clearAnimationQueue, setCurrentlyShuffling, markPlayerReceivingEffect, markPlayerBeingDamaged
} = slice.actions;

let curAnimationExpirationHandler = null;
const startAnimation = (anim) => (dispatch, getState) => {
  if (anim.onStart) {
    const onStartEvents = !Array.isArray(anim.onStart) ? [anim.onStart] : anim.onStart;
    for (const event of onStartEvents) {
      if (typeof event === 'object' && workaroundMapForUnserializableEventsInDispatch[event.func]) {
        dispatch(workaroundMapForUnserializableEventsInDispatch[event.func](...event.args));
      } else {
        dispatch(event);
      }
    }
  }

  curAnimationExpirationHandler = setTimeout(() => dispatch(updateAnimationQueue()), anim.duration);
}

export const updateAnimationQueue = () => (dispatch, getState) => {
  if (curAnimationExpirationHandler) {
    clearTimeout(curAnimationExpirationHandler);
    curAnimationExpirationHandler = null;
  }

  if (getState().animation.animationQueue.length === 0) {
    return;
  }

  const runningAnimation = getState().animation.animationQueue[0];
  dispatch(shiftAnimationQueue());
  const newAnimQueue = getState().animation.animationQueue; // We need to call getState() again because it changed

  if (runningAnimation.onEnd) {
    const onEndEvents = !Array.isArray(runningAnimation.onEnd) ? [runningAnimation.onEnd] : runningAnimation.onEnd;
    for (const event of onEndEvents) {
      if (typeof event === 'object' && workaroundMapForUnserializableEventsInDispatch[event.func]) {
        dispatch(workaroundMapForUnserializableEventsInDispatch[event.func](...event.args));
      } else {
        dispatch(event);
      }
    }
  }
  if (newAnimQueue.length > 0) {
    dispatch(startAnimation(newAnimQueue[0]));
  }
};

let animId = 0; // Ever-incrementing animation id
export const addToAnimationQueue = anim => (dispatch, getState) => {
  anim.iid = ++animId;
  const state = getState();
  if (state.animation.animationQueue.length === 0) {
    dispatch(startAnimation(anim));
  }

  dispatch(slice.actions.addToAnimationQueueInternal(anim));
};

export const startAnimatedPlayerDataChange = (events, delayGive) => (dispatch, getState) => {
  for (let event of events) {
    const [targetPlayerId, targetProperty, targetDiff] = event;
    if (targetDiff === 0) {
      continue; // Da fuck
    }

    const showEffect = () => {
      const player = getState().game.players.find((player) => player.id === targetPlayerId);
      let targetValue = player[targetProperty] + targetDiff;
      if (targetProperty === "health") {
        targetValue = Math.min(getMaxPlayerHealth(player), Math.max(0, targetValue));
      } else if (targetProperty === "energy") {
        targetValue = Math.max(0, targetValue);
      } else if (targetProperty === "stars") {
        targetValue = Math.max(0, Math.min(20, targetValue));
      }

      if (targetValue !== player[targetProperty]) {
        dispatch(setPlayerPropertyById({
          id: targetPlayerId,
          property: targetProperty,
          value: targetValue
        }));

        dispatch(markPlayerReceivingEffect({ id: player.id, state: true }));
        setTimeout(() => {
          dispatch(markPlayerReceivingEffect({ id: player.id, state: false }));
        }, 500);

        if (targetProperty === "health" && targetDiff < 0) {
          dispatch(markPlayerBeingDamaged({ id: player.id, state: true }));
          setTimeout(() => {
            dispatch(markPlayerBeingDamaged({ id: player.id, state: false }));
          }, 400);
        }
      }
    };

    setTimeout(showEffect, delayGive);
  }
};

export const selectIsRollingDice = state => {
  const anim = selectCurrentAnimation(state);
  return anim && anim.type === "dice-roll";
}
export const selectCurrentAnimation = state => state.animation.animationQueue.length > 0 ? state.animation.animationQueue[0] : null;
export const selectIsCurrentlyShuffling = state => state.animation.isCurrentlyShuffling;

export default slice.reducer;

// Work around the fact that Redux doesn't like me sending any function through dispatch
// But I can send the name though, so I win.
const workaroundMapForUnserializableEventsInDispatch = {
  "startAnimatedPlayerDataChange": startAnimatedPlayerDataChange
};
